import { useCallback, useEffect, useMemo, useState } from 'react';
import useAuth from './useAuth';
import RELEASE_NOTES from '../releaseNotes';
import { PATH_DASHBOARD } from '../routes/paths';

// ----------------------------------------------------------------------
// "What's new": which release notes this user can see and whether the latest one is unseen.
// "Seen" = last version the user closed, kept in this browser per user (no API involved).

const SEEN_EVENT = 'farmup-whats-new-seen';

// -1 / 0 / 1, numeric per part: '1.10.0' is newer than '1.9.3'
export const compareVersions = (a = '', b = '') => {
  const pa = String(a).split('.').map((part) => parseInt(part, 10) || 0);
  const pb = String(b).split('.').map((part) => parseInt(part, 10) || 0);
  for (let i = 0; i < Math.max(pa.length, pb.length); i += 1) {
    const diff = (pa[i] || 0) - (pb[i] || 0);
    if (diff !== 0) return diff > 0 ? 1 : -1;
  }
  return 0;
};

const storageKey = (user) => `whatsNewSeen_${user?.database || ''}_${user?.id ?? ''}`;

const readSeen = (key) => {
  try {
    return localStorage.getItem(key) || '';
  } catch (e) {
    return '';
  }
};

// Help is for everyone; a screen only when the user has that page in their permissions
const canOpen = (link, permissions) => {
  if (!link || link.help) return true;
  if (!PATH_DASHBOARD[link.module]?.[link.page]) return false;
  return (permissions || []).some((perm) => perm.module === link.module && perm.pages?.includes(link.page));
};

// Text in the user's language, English when that language is missing
export const pickText = (texts, lang) => (texts ? texts[lang] || texts.en || '' : '');

export const linkPath = (link) => {
  if (!link || link.help) return null;
  const path = PATH_DASHBOARD[link.module]?.[link.page];
  if (!path) return null;
  return link.tab ? `${path}?tab=${link.tab}` : path;
};

export default function useWhatsNew() {
  const { user } = useAuth();
  const currentVersion = process.env.REACT_APP_VERSION || '';
  const key = storageKey(user);

  const [seen, setSeen] = useState(() => readSeen(key));

  // Same value in the menu button and the window, also across browser tabs
  useEffect(() => {
    const refresh = () => setSeen(readSeen(key));
    refresh();
    window.addEventListener(SEEN_EVENT, refresh);
    window.addEventListener('storage', refresh);
    return () => {
      window.removeEventListener(SEEN_EVENT, refresh);
      window.removeEventListener('storage', refresh);
    };
  }, [key]);

  // Releases up to the running version, newest first, with only the items this user can open
  const releases = useMemo(
    () =>
      RELEASE_NOTES.filter((release) => compareVersions(release.version, currentVersion) <= 0)
        .sort((a, b) => compareVersions(b.version, a.version))
        .map((release) => ({
          ...release,
          items: release.items.filter((item) => canOpen(item.link, user?.permissions)),
        }))
        .filter((release) => release.items.length > 0),
    [currentVersion, user?.permissions]
  );

  const latest = releases[0] || null;
  const hasUnseen = !!user && !!latest && compareVersions(latest.version, seen || '0') > 0;

  const markSeen = useCallback(() => {
    if (!latest) return;
    try {
      localStorage.setItem(key, latest.version);
    } catch (e) {
      // private mode / storage blocked: the window may show again next time, nothing else breaks
    }
    window.dispatchEvent(new Event(SEEN_EVENT));
  }, [key, latest]);

  return { releases, latest, hasUnseen, markSeen, currentVersion };
}
